import { useSyncExternalStore } from "react";

export const RUN_SETTINGS_KEY = "codeshare:run-settings";

/** Per-runner settings, kept in this browser only (never shared with peers). */
export interface RunSettings {
  /** Runner id → the argument line, as typed. */
  args: Record<string, string>;
  /** Runners allowed to load npm packages from the CDN (JavaScript only, for now). */
  packages?: string[];
}

/** Where npm packages load from when allowed (also allowed by the page's CSP). */
export const PACKAGE_CDN = "https://esm.sh";

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

/**
 * Splits an argument line like a shell would: whitespace separates arguments, single quotes keep
 * text as is, double quotes keep spaces (with \" and \\ escapes), and a backslash escapes the
 * next character outside quotes. No expansion of any kind.
 */
export function parseArgs(line: string): string[] {
  const args: string[] = [];
  let current = "";
  let inArg = false;
  let quote: "'" | '"' | undefined;
  for (let i = 0; i < line.length; i++) {
    const c = line[i] as string;
    if (quote === "'") {
      if (c === "'") quote = undefined;
      else current += c;
    } else if (quote === '"') {
      if (c === '"') quote = undefined;
      else if (c === "\\" && (line[i + 1] === '"' || line[i + 1] === "\\")) current += line[++i];
      else current += c;
    } else if (c === "'" || c === '"') {
      quote = c;
      inArg = true;
    } else if (c === "\\" && i + 1 < line.length) {
      current += line[++i];
      inArg = true;
    } else if (/\s/.test(c)) {
      if (inArg) args.push(current);
      current = "";
      inArg = false;
    } else {
      current += c;
      inArg = true;
    }
  }
  if (inArg) args.push(current);
  return args;
}

function safeLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export class RunSettingsStore {
  #settings: RunSettings;
  readonly #listeners = new Set<() => void>();

  constructor(private readonly storage: Storage | null = safeLocalStorage()) {
    this.#settings = { args: {} };
    try {
      const stored = JSON.parse(storage?.getItem(RUN_SETTINGS_KEY) ?? "null");
      if (stored && typeof stored.args === "object" && stored.args) {
        for (const [id, line] of Object.entries(stored.args)) {
          if (typeof line === "string") this.#settings.args[id] = line;
        }
      }
      if (Array.isArray(stored?.packages)) {
        const ids = stored.packages.filter((id: unknown) => typeof id === "string");
        if (ids.length > 0) this.#settings.packages = ids;
      }
    } catch {}
  }

  get settings(): RunSettings {
    return this.#settings;
  }

  /** The arguments to run `runnerId` programs with. */
  argsFor(runnerId: string): string[] {
    return parseArgs(this.#settings.args[runnerId] ?? "");
  }

  setArgs(runnerId: string, line: string): void {
    const args = { ...this.#settings.args };
    if (line.trim()) args[runnerId] = line;
    else delete args[runnerId];
    this.#save({ ...this.#settings, args });
  }

  /** Whether `runnerId` programs may load npm packages from the CDN. */
  packagesAllowed(runnerId: string): boolean {
    return this.#settings.packages?.includes(runnerId) ?? false;
  }

  setPackagesAllowed(runnerId: string, allowed: boolean): void {
    const ids = (this.#settings.packages ?? []).filter((id) => id !== runnerId);
    if (allowed) ids.push(runnerId);
    const { packages: _old, ...rest } = this.#settings;
    this.#save(ids.length > 0 ? { ...rest, packages: ids } : rest);
  }

  #save(settings: RunSettings): void {
    this.#settings = settings;
    try {
      this.storage?.setItem(RUN_SETTINGS_KEY, JSON.stringify(this.#settings));
    } catch {}
    for (const listener of this.#listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}

let store: RunSettingsStore | undefined;
export function getRunSettings(): RunSettingsStore {
  store ??= new RunSettingsStore();
  return store;
}

export function useRunSettings(): RunSettings {
  const settings = getRunSettings();
  return useSyncExternalStore(
    (listener) => settings.subscribe(listener),
    () => settings.settings,
  );
}
